import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "sonner";
import { X } from "lucide-react";
import Wordmark from "./Wordmark";
import { api } from "../services";

// Functional COMPARE WITH A FRIEND. One purpose: get a real Challenge link
// in front of a friend. The recipient never starts from zero — they land
// straight inside the same Test. Used both post-Profile and as the Match
// "second transmission". (A single Face-Off share is a separate, lower-value
// primitive elsewhere in the product — deliberately not offered here.)

export default function CompareFriendModal({ open, runId, profile, reducedMotion, source, onClose }) {
  const [challengeUrl, setChallengeUrl] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || !runId || challengeUrl) return;
    setBusy(true);
    api
      .createChallenge(runId)
      .then((r) => {
        setChallengeUrl(`${window.location.origin}/?challenge=${r.challenge_id}`);
        api.logEvent(source === "match" ? "second_challenge_created" : "challenge_created", runId, {});
      })
      .catch(() => toast.error("Couldn't create a challenge link"))
      .finally(() => setBusy(false));
  }, [open, runId, challengeUrl, source]);

  // Escape closes on desktop — dismissal only, never mutates run state.
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const copy = async (url, label) => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success(`${label} copied`, { description: "Anyone who opens it starts right inside the Test." });
    } catch {
      toast.message(label, { description: url });
    }
  };

  const nativeOrCopy = async () => {
    if (!challengeUrl) return;
    if (navigator.share) {
      try {
        await navigator.share({ title: "TASTE TEST", text: "I found my taste. Can you match it?", url: challengeUrl });
        return;
      } catch { /* dismissed */ }
    }
    copy(challengeUrl, "Challenge link");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div data-testid="compare-modal" className="fixed inset-0 z-[70] flex items-end justify-center p-3 sm:items-end sm:justify-end sm:p-8"
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.2 }}>
          {/* Subtle backdrop — the panel takes focus while the screen behind
              stays understandable (outside-click / tap dismisses). */}
          <div data-testid="compare-backdrop" className="absolute inset-0 bg-black/45 backdrop-blur-[3px]" onClick={onClose} />
          <motion.div role="dialog" aria-modal="true" className="relative z-10 w-full max-w-[460px] bg-surface border border-white/12 rounded-3xl p-5 sm:p-6 pb-[calc(env(safe-area-inset-bottom)+20px)] shadow-[0_28px_90px_rgba(0,0,0,0.62)]"
            initial={reducedMotion ? { opacity: 0 } : { y: 40, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            exit={reducedMotion ? { opacity: 0 } : { y: 40, opacity: 0 }} transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}>
            <div className="flex items-center justify-between mb-4">
              <span className="font-display font-black uppercase tracking-[0.04em] text-bone text-base">
                {source === "match" ? "Challenge someone else" : "Compare with a friend"}
              </span>
              <button data-testid="compare-close" onClick={onClose} className="text-silver hover:text-bone p-1"><X size={18} /></button>
            </div>

            {/* One sharp idea, not a Profile paragraph squeezed into a card.
                Flex regions (not independently-absolute blocks) so the
                headline can never grow into the footer divider, whatever
                length oneLine happens to be. */}
            <div className="relative flex flex-col rounded-2xl overflow-hidden border border-white/10 tt-grain aspect-[9/16] max-h-[42vh] mx-auto px-5 py-4"
              style={{ background: "linear-gradient(160deg,#141019,#070709)" }}>
              <div className="shrink-0 flex items-center justify-between">
                <Wordmark variant="horizontal" className="h-3" />
                <span className="font-mono text-[9px] tracking-[0.15em] text-subtle">24 / 24</span>
              </div>

              <div className="flex-1 min-h-0 flex flex-col justify-center py-3">
                <p className="shrink-0 font-mono text-[9px] tracking-[0.3em] uppercase text-silver mb-2.5">Match my taste</p>
                <p className="font-display font-black leading-snug tracking-[-0.02em] text-bone text-base line-clamp-3">
                  {profile?.oneLine}
                </p>
              </div>

              <div className="shrink-0">
                <div className="h-px w-full mb-2.5" style={{ background: "linear-gradient(90deg,var(--tt-left),var(--tt-right))" }} />
                <p className="font-display font-black uppercase text-bone text-sm tracking-tight">Can you match my taste?</p>
              </div>
            </div>

            <button data-testid="share-taste-btn" disabled={busy || !challengeUrl} onClick={nativeOrCopy}
              className="mt-5 w-full rounded-full py-3.5 font-display font-black uppercase tracking-[0.06em] text-sm text-[#05070a] disabled:opacity-50"
              style={{ background: "linear-gradient(90deg,var(--tt-left),var(--tt-right))" }}>
              {busy ? "Creating link…" : "Share your taste"}
            </button>
            <p className="mt-3 text-center font-body text-[11px] text-subtle">
              They start inside the same Test. No signup.
            </p>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
